import { BookingCardTheme } from './themesCrafted';
import { motion } from 'framer-motion';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { CheckCircle2, Hash, Instagram } from 'lucide-react';
import { formatMemberSince } from '@/utils/dateFormatting';
import { useFitText } from '@/hooks/useFitText';

export interface BookingCardRichGradientFrontProps {
  businessName: string;
  businessInstagramHandle: string | null;
  businessLogoUrl?: string | null;
  clientFullName: string;
  displayedVisits: number;
  memberSinceDate: string | null;
  publicConfirmationCode: string;
  theme: BookingCardTheme;
}

export function BookingCardRichGradientFront({
  businessName,
  businessInstagramHandle,
  businessLogoUrl,
  clientFullName,
  displayedVisits,
  memberSinceDate,
  publicConfirmationCode,
  theme,
}: BookingCardRichGradientFrontProps) {
  const prefersReducedMotion = useReducedMotion();
  const memberSince = formatMemberSince(memberSinceDate);
  const { ref: nameRef, fontSize: nameFontSize } = useFitText(clientFullName, 24, 10);
  const { ref: bizRef, fontSize: bizFontSize } = useFitText(businessName, 18, 10);

  return (
    <div
      className="relative w-full h-full rounded-3xl overflow-hidden p-6 flex flex-col"
      style={{
        background: theme.gradientFrom,
        border: `1px solid ${theme.borderColor}`,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.16), 0 12px 40px rgba(0, 0, 0, 0.24), 0 0 1px rgba(0, 0, 0, 0.2)',
      }}
    >
      {/* Rich Gradient Layer - Animated with darker, richer colors and faster speed */}
      {!prefersReducedMotion && (
        <motion.div
          className="absolute pointer-events-none"
          style={{
            top: '-40%',
            left: '-20%',
            width: '140%',
            height: '140%',
            zIndex: 0,
            opacity: 0.55,
            maskImage: 'radial-gradient(ellipse at center, black 40%, transparent 80%)',
            WebkitMaskImage: 'radial-gradient(ellipse at center, black 40%, transparent 80%)',
          }}
          animate={{
            translateX: ['-10%', '10%', '-10%'],
            translateY: ['-8%', '8%', '-8%'],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 6,
            ease: 'easeInOut',
            repeat: Infinity,
          }}
        >
          {/* Rich gradient blobs - extreme saturation, neon-level colors */}
          <div
            className="absolute inset-0"
            style={{
              background: `
                radial-gradient(circle at 30% 40%, #FF0055 0%, transparent 50%),
                radial-gradient(circle at 70% 50%, #CC00FF 0%, transparent 50%),
                radial-gradient(circle at 50% 70%, #FF3300 0%, transparent 50%),
                radial-gradient(circle at 60% 30%, #00CCFF 0%, transparent 50%),
                radial-gradient(circle at 40% 60%, #00FF66 0%, transparent 50%)
              `,
              filter: 'blur(80px)',
            }}
          />
        </motion.div>
      )}

      {/* Noise Overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          zIndex: 1,
          opacity: 0.04,
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Content Layer */}
      <div className="relative flex flex-col h-full" style={{ zIndex: 2 }}>
        {/* SECTION 1 — HEADER */}
        <div className="flex justify-between items-start mb-4">
          {/* Left: Business Name + Instagram */}
          <div className="flex-1 min-w-0 pr-2">
            <div className="flex items-center gap-2">
              <div
                ref={bizRef}
                className="font-bold tracking-tight leading-tight"
                style={{
                  fontSize: `${bizFontSize}px`,
                  color: theme.textPrimary,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                }}
              >
                {businessName}
              </div>
              {businessInstagramHandle && (
                <a
                  href={`https://instagram.com/${businessInstagramHandle.replace('@', '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-shrink-0 hover:opacity-70 transition-opacity"
                  aria-label={`Visit ${businessName} on Instagram`}
                >
                  <Instagram
                    style={{
                      color: theme.textSecondary,
                      width: 'clamp(1rem, 2vw, 1.25rem)',
                      height: 'clamp(1rem, 2vw, 1.25rem)',
                    }}
                  />
                </a>
              )}
            </div>
          </div>

          {/* Right: Visit Counter */}
          <div className="flex flex-col items-end flex-shrink-0">
            <div className="flex items-center gap-1">
              <div
                className="font-bold tabular-nums leading-none"
                style={{
                  fontSize: 'clamp(1.125rem, 2.5vw + 0.5rem, 1.25rem)',
                  color: theme.textPrimary,
                }}
              >
                {displayedVisits}
              </div>
              <CheckCircle2
                style={{
                  color: theme.accentColor,
                  width: 'clamp(1.25rem, 2.5vw, 1.5rem)',
                  height: 'clamp(1.25rem, 2.5vw, 1.5rem)',
                }}
                fill="currentColor"
              />
            </div>
            <div
              className="uppercase tracking-wider font-medium mt-0.5"
              style={{
                fontSize: 'clamp(0.625rem, 1.25vw, 0.75rem)',
                color: theme.textSecondary,
              }}
            >
              VISITS
            </div>
          </div>
        </div>

        {/* SECTION 2 — PRIMARY MOMENT */}
        <div className="flex-1 flex flex-col items-center justify-center text-center gap-3">
          {businessLogoUrl && (
            <img
              src={businessLogoUrl}
              alt={`${businessName} logo`}
              className="max-h-20 sm:max-h-24 w-auto object-contain"
              style={{
                maxWidth: '60%',
                filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.2))',
              }}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          )}
          <div>
            <div
              className="font-bold tracking-tight"
              style={{
                fontSize: 'clamp(2rem, 5vw + 1rem, 2.5rem)',
                color: theme.textPrimary,
              }}
            >
              Confirmed
            </div>
            <div
              className="uppercase tracking-wider mt-1"
              style={{
                fontSize: 'clamp(0.625rem, 1.25vw, 0.75rem)',
                color: theme.textSecondary,
              }}
            >
              Your appointment is booked
            </div>
          </div>
        </div>

        {/* SECTION 3 — FOOTER */}
        <div className="mt-auto flex justify-between items-end gap-2">
          <div className="flex-1 min-w-0">
            <div
              ref={nameRef}
              className="font-bold uppercase tracking-wide mb-1"
              style={{
                fontSize: `${nameFontSize}px`,
                color: theme.textPrimary,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
              }}
            >
              {clientFullName}
            </div>
            {memberSince && (
              <div
                className="uppercase tracking-wider mb-2"
                style={{
                  fontSize: 'clamp(0.625rem, 1.25vw, 0.75rem)',
                  color: theme.textSecondary,
                }}
              >
                MEMBER SINCE: {memberSince}
              </div>
            )}
            <div className="flex items-center gap-1.5 min-w-0">
              <Hash
                className="flex-shrink-0"
                style={{
                  color: theme.textSecondary,
                  width: 'clamp(0.75rem, 1.5vw, 0.875rem)',
                  height: 'clamp(0.75rem, 1.5vw, 0.875rem)',
                }}
              />
              <div
                className="font-medium tracking-wide truncate"
                style={{
                  fontSize: 'clamp(0.75rem, 1.75vw, 0.875rem)',
                  color: theme.textSecondary,
                }}
              >
                {publicConfirmationCode}
              </div>
            </div>
          </div>
          <div
            className="font-medium tracking-wide text-right flex-shrink-0 whitespace-nowrap"
            style={{
              fontSize: 'clamp(0.75rem, 1.75vw, 0.875rem)',
              color: theme.textSecondary,
            }}
          >
            MADE WITH FIGGY
          </div>
        </div>
      </div>
    </div>
  );
}
